/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import { useCallback, useContext } from "react";

import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ToastContext } from "@forge/common";

import { RootState } from "../store";
import { LinkGatewayResult } from "../models";
import { GatewayStatus, LOCALE_CONST } from "../utils/constants";
import { isValidSiteId } from "../utils/siteContext";
import { notifyParent } from "../utils/notifyParent";
import { buildLinkGatewayPayload, gatewayErrorStatusMessages } from "../utils/utility";
import { useLazyGetDeviceDetailsQuery, useUpdateGatewayMutation } from "../services/api";

interface LinkGatewayOptions {
  // Show the success/failure toasts here; the caller shows its own when false.
  notify?: boolean;
}

const TOAST_LIFETIME = 5000;

/**
 * Links a gateway to the selected site: fetch the latest device details, build the payload
 * with the site and customer, PUT, then tell the parent. Site eligibility is enforced by the
 * parent (Assign gateway is disabled unless the site is provisioned), so it is not re-checked here.
 */
export const useLinkGatewayToSite = () => {
  const { t } = useTranslation();
  const { addSuccess, addError } = useContext(ToastContext);

  const { siteId, siteName, customerOrgId, customerName, orgLists } = useSelector(
    (state: RootState) => state.global,
  );

  const [fetchDeviceDetails] = useLazyGetDeviceDetailsQuery();
  const [updateGateway] = useUpdateGatewayMutation();

  const showToast = useCallback(
    (type: "success" | "error", content: string) => {
      const toast = {
        id: "uop-toast-message",
        className: "uop-toast-message",
        title: "",
        subtitle: "",
        showClose: true,
        showDismiss: false,
        content,
        lifetime: TOAST_LIFETIME,
      };

      if (type === "success") {
        addSuccess(toast);
      } else {
        addError(toast);
      }
    },
    [addSuccess, addError],
  );

  const linkGateway = useCallback(
    async (
      deviceId: string,
      { notify = true }: LinkGatewayOptions = {},
    ): Promise<LinkGatewayResult> => {
      const convertFn = (value: string) => t(value);

      const fail = (status?: number): LinkGatewayResult => {
        if (notify) {
          showToast(
            "error",
            gatewayErrorStatusMessages(convertFn, t(LOCALE_CONST.gatewayLinkFailed), status),
          );
        }
        return "failed";
      };

      if (!deviceId || !isValidSiteId(siteId) || !customerOrgId) {
        return fail();
      }

      try {
        const deviceResp = await fetchDeviceDetails({ deviceId }, false);

        if (deviceResp.error || !deviceResp.data) {
          return fail((deviceResp.error as { status?: number })?.status);
        }

        const device = deviceResp.data;

        // A pending gateway may not have the details the update needs.
        if (device.status === GatewayStatus.Pending) {
          if (notify) {
            showToast("error", t(LOCALE_CONST.gatewayPendingCannotLink));
          }
          return "pending";
        }

        // Race guard: the gateway was linked to another customer after the list was loaded.
        if (device.organizationId && device.organizationId !== customerOrgId) {
          if (notify) {
            showToast("error", t(LOCALE_CONST.gatewayLinkSkipped));
          }
          return "skipped";
        }

        // Already linked to this site: nothing to send.
        if (Array.isArray(device.site) && device.site.some((item: { id?: string }) => item?.id === siteId)) {
          if (notify) {
            showToast("error", t(LOCALE_CONST.gatewayLinkSkipped));
          }
          return "skipped";
        }

        const customer = orgLists?.find((org) => org.value === customerOrgId);
        const payload = buildLinkGatewayPayload(
          device,
          { id: siteId, name: siteName },
          customerOrgId,
          customerName || customer?.text || "",
        );

        const updateResp = await updateGateway({ deviceId, gateway: payload });

        if ("error" in updateResp && updateResp.error) {
          return fail((updateResp.error as { status?: number })?.status);
        }

        if (notify) {
          showToast(
            "success",
            t(LOCALE_CONST.gatewayLinkedSuccess, {
              gatewayName: device.name,
              siteName,
            }),
          );
        }

        notifyParent({ gatewayLinked: true, siteId });

        return "linked";
      } catch (error) {
        return fail();
      }
    },
    [
      t,
      siteId,
      siteName,
      customerOrgId,
      customerName,
      orgLists,
      fetchDeviceDetails,
      updateGateway,
      showToast,
    ],
  );

  return { linkGateway };
};
